import React from "react";
import { Box, Typography, TextField, Button } from "@mui/material";
import { useTranslation } from "../../hooks/useTranslation";
import PhoneIcon from "@mui/icons-material/Phone";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SecurityIcon from "@mui/icons-material/Security";

interface PhoneStepProps {
  phone: string;
  validation: { valid: boolean; error: string };
  onChange: (field: string, value: string) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  isMobile: boolean;
}

const PhoneStep: React.FC<PhoneStepProps> = ({
  phone,
  validation,
  onChange,
  onSubmit,
  isSubmitting,
  isMobile,
}) => {
  const { t } = useTranslation();

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (phone.trim() !== "" && validation.valid && !isSubmitting) {
        onSubmit();
      }
    }
  };

  const formatPhoneDisplay = (value: string) => {
    if (value.length <= 3) return value;
    if (value.length <= 6) return `(${value.slice(0, 3)}) ${value.slice(3)}`;
    return `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`;
  };

  const isValid = validation.valid && phone.trim() !== "";
  const hasError = !validation.valid && phone.length > 0;

  return (
    <Box>
      <Typography
        variant="h4"
        align="center"
        sx={{
          mb: 4,
          fontWeight: 700,
          color: "#1e293b",
          fontSize: { xs: "1.2rem", md: "1.5rem" },
        }}
      >
        9. {t("form.ninthQuestion.question")}
        <span style={{ color: "#ef4444" }}>*</span>
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
          }}
        >
          <PhoneIcon sx={{ color: "#64748b", fontSize: 28 }} />
          <Typography
            variant="body2"
            sx={{
              color: "#64748b",
              fontWeight: 500,
            }}
          >
            Your phone number
          </Typography>
        </Box>

        <TextField
          fullWidth
          placeholder={t("form.ninthQuestion.placeholder")}
          value={formatPhoneDisplay(phone)}
          onChange={(e) => {
            const newValue = e.target.value;
            const digitsOnly = newValue.replace(/\D/g, "");
            if (digitsOnly.length <= 10) {
              onChange("phone", digitsOnly);
            }
          }}
          onKeyDown={handleKeyDown}
          error={hasError}
          disabled={isSubmitting}
          inputProps={{
            maxLength: 14,
            inputMode: "numeric",
            pattern: "[0-9]*",
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              backgroundColor: isSubmitting ? "#f1f5f9" : "#f8fafc",
              fontSize: "1.125rem",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: isSubmitting ? "#f1f5f9" : "#f1f5f9",
              },
              "&.Mui-focused": {
                backgroundColor: "#ffffff",
                "& fieldset": {
                  borderColor: isValid ? "#22c55e" : "#3b82f6",
                  borderWidth: "2px",
                },
              },
              "&.Mui-error": {
                "& fieldset": {
                  borderColor: "#ef4444",
                  borderWidth: "2px",
                },
              },
            },
            "& .MuiOutlinedInput-input": {
              padding: "16px 14px",
            },
          }}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 1.5,
            px: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {hasError && (
              <>
                <ErrorOutlineIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                <Typography variant="caption" color="error">
                  {validation.error}
                </Typography>
              </>
            )}
            {isValid && (
              <>
                <CheckCircleIcon sx={{ fontSize: 16, color: "#22c55e" }} />
                <Typography
                  variant="caption"
                  sx={{ color: "#22c55e", fontWeight: 500 }}
                >
                  Valid phone number!
                </Typography>
              </>
            )}
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: phone.length === 10 ? "#22c55e" : "#94a3b8",
              fontWeight: 500,
            }}
          >
            {phone.length}/10 digits
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          p: 2.5,
          backgroundColor: "#fef3c7",
          borderRadius: "8px",
          border: "1px solid #fcd34d",
          mb: 3,
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1 }}
        >
          <SecurityIcon sx={{ color: "#f59e0b", fontSize: 20, mt: 0.2 }} />
          <Typography
            variant="body2"
            sx={{
              color: "#92400e",
              fontSize: "0.875rem",
              fontWeight: 600,
              lineHeight: 1.5,
            }}
          >
            Important Legal Notice
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: "#78350f",
            fontSize: { xs: "0.75rem", md: "0.8rem" },
            lineHeight: 1.6,
            pl: 4.5,
          }}
        >
          {t("form.ninthQuestion.tcpa1")}{" "}
          <span style={{ fontWeight: 600 }}>
            {t("form.ninthQuestion.tcpa2")}
          </span>{" "}
          {t("form.ninthQuestion.tcpa3")}{" "}
          <a
            href="https://www.caraccidentcounsel.com/network-of-lawyers-and-advocates/"
            style={{
              color: "#92400e",
              textDecoration: "underline",
              fontWeight: 500,
            }}
          >
            {t("form.ninthQuestion.tcpa4")}
          </a>{" "}
          {t("form.ninthQuestion.tcpa5")}{" "}
          <a
            href="https://www.caraccidentcounsel.com/privacy-policy/"
            style={{
              color: "#92400e",
              textDecoration: "underline",
              fontWeight: 500,
            }}
          >
            {t("form.ninthQuestion.tcpa6")}
          </a>
          {t("form.ninthQuestion.tcpa7")}{" "}
          <a
            href="https://www.caraccidentcounsel.com/terms-of-service/"
            style={{
              color: "#92400e",
              textDecoration: "underline",
              fontWeight: 500,
            }}
          >
            {t("form.ninthQuestion.tcpa8")}
          </a>
          .<span style={{ color: "#dc2626" }}>*</span>
        </Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Button
          variant="contained"
          id="SubmitForm"
          size="large"
          onClick={onSubmit}
          disabled={phone.trim() === "" || !validation.valid || isSubmitting}
          sx={{
            minWidth: "200px",
            py: 1.5,
            px: 4,
            fontWeight: 600,
            fontSize: "1rem",
            borderRadius: "10px",
            backgroundColor: "#3b82f6",
            textTransform: "none",
            boxShadow:
              isValid && !isSubmitting
                ? "0 4px 14px 0 rgba(59, 130, 246, 0.39)"
                : "none",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "#2563eb",
              boxShadow: "0 6px 20px rgba(59, 130, 246, 0.5)",
              transform: isSubmitting ? "none" : "translateY(-1px)",
            },
            "&:disabled": {
              backgroundColor: "#cbd5e1",
              color: "#94a3b8",
              boxShadow: "none",
            },
          }}
        >
          {isSubmitting ? "Submitting..." : t("form.ninthQuestion.submit")}
        </Button>
      </Box>

      {isSubmitting && (
        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: "#eff6ff",
            borderRadius: "8px",
            border: "1px solid #bfdbfe",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              border: "3px solid #3b82f6",
              borderTopColor: "transparent",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
          <Typography
            sx={{
              color: "#3b82f6",
              fontWeight: 500,
              fontSize: "0.95rem",
            }}
          >
            Processing your submission...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PhoneStep;
